import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toProdProfile } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { ProfileFields } from "@/components/ProfileFields";
import { PhotoManager } from "@/components/PhotoManager";
import { Button } from "@/components/ui/button";
import { PrivacyControls } from "@/components/PrivacyControls";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Save, Eye, Crown, ShieldCheck, MapPin, BadgeCheck, Pencil, Images, CheckCircle2, Sparkles, Mail, MailWarning, ChevronRight } from "lucide-react";

const TABS = [
  { key: "edit", label: "Edit details", icon: Pencil },
  { key: "photos", label: "Photo & visibility", icon: Images },
  { key: "preview", label: "Preview", icon: Eye },
];

export default function MyProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(user?.profile || {});
  const [tab, setTab] = useState("edit");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const completeness = Number(user?.raw?.profileCompleteness ?? 0);
  const missing = user?.raw?.completenessMissing || [];
  const initials = (p.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const emailVerified = !!user?.email_verified;
  const badgeEarned = !!user?.verified;

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", toProdProfile(p)); await refreshUser(); toast.success("Profile saved and synced to your account"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };


  return (
    <div>
      {/* Verify your email banner */}
      {!emailVerified && (
        <button onClick={() => navigate("/verify-email")} data-testid="verify-email-banner" className="mb-4 flex w-full items-center gap-3 rounded-2xl bg-amber-100/70 p-4 text-left ring-1 ring-amber-300/50 transition-colors hover:bg-amber-100">
          <MailWarning className="h-6 w-6 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-amber-900">Verify your email</p>
            <p className="text-sm text-amber-800/90">Confirm your email to earn your verified badge and unlock reporting.</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-amber-600" />
        </button>
      )}

      {/* Hero card */}
      <div className="mb-6 overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm">
        <div className="h-24 bg-gradient-to-r from-emerald-700 via-emerald-800 to-emerald-950" />
        <div className="px-6 pb-6">
          <div className="-mt-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <Avatar className="h-24 w-24 border-4 border-white shadow-md">
                {p.photo && <AvatarImage src={p.photo} alt="" className="object-cover" />}
                <AvatarFallback className="bg-emerald-800 font-serif text-2xl text-amber-200">{initials}</AvatarFallback>
              </Avatar>
              <div className="pb-1">
                <h1 className="flex items-center gap-2 font-serif text-3xl font-bold text-emerald-950">
                  {p.name || "Your name"}{p.age ? `, ${p.age}` : ""}
                  {user?.verified && <BadgeCheck className="h-5 w-5 text-emerald-600" />}
                </h1>
                <p className="flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {[p.city, p.country].filter(Boolean).join(", ") || "Add your location"}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {user?.is_premium && <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500"><Crown className="h-3.5 w-3.5" /> Premium</Badge>}
              {p.wali?.name && <Badge className="gap-1 bg-indigo-600 text-white hover:bg-indigo-600"><ShieldCheck className="h-3.5 w-3.5" /> Wali</Badge>}
              <Button variant="outline" onClick={() => navigate(`/profiles/${user?.id}`)} data-testid="view-public-profile-btn" className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint"><Eye className="mr-1.5 h-4 w-4" /> View public</Button>
            </div>
          </div>

          {/* Completeness */}
          <div className="mt-5 rounded-2xl bg-mint/50 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 font-semibold text-emerald-900"><Sparkles className="h-4 w-4 text-amber-600" /> Profile strength</span>
              <span className="font-bold text-emerald-800" data-testid="profile-completeness">{completeness}%</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-amber-500 transition-all" style={{ width: `${completeness}%` }} />
            </div>
            {missing.length > 0 && (
              <p className="mt-2 text-xs text-emerald-800/80">To reach 100%: {missing.join(" · ")}</p>
            )}
          </div>
        </div>
      </div>

      {/* Verification */}
      <div className="mb-6" data-testid="verification-section">
        <h2 className="mb-3 font-serif text-2xl font-bold text-emerald-950">Verification</h2>
        <div className="overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm">
          <div className="flex items-center gap-3 p-4">
            <Mail className="h-5 w-5 text-slate-500" />
            <span className="flex-1 font-semibold text-emerald-950">Email verified</span>
            {emailVerified ? (
              <span data-testid="email-verified-pill" className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Verified</span>
            ) : (
              <Button onClick={() => navigate("/verify-email")} data-testid="profile-verify-email-btn" size="sm" className="rounded-full bg-amber-600 text-white hover:bg-amber-700">Verify now</Button>
            )}
          </div>
          <div className="h-px bg-emerald-900/10" />
          <div className="flex items-center gap-3 p-4">
            <ShieldCheck className="h-5 w-5 text-slate-500" />
            <span className="flex-1 font-semibold text-emerald-950">Verified badge</span>
            {badgeEarned ? (
              <span data-testid="verified-badge-pill" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700"><BadgeCheck className="h-4 w-4" /> Earned</span>
            ) : (
              <span className="text-sm font-medium text-slate-500">Verify email + complete profile</span>
            )}
          </div>
        </div>
      </div>

      {/* Pill tabs */}
      <div className="mb-6 flex flex-wrap gap-2 rounded-2xl bg-white p-1.5 shadow-sm ring-1 ring-emerald-900/5 sm:inline-flex">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-emerald-800 text-white shadow" : "text-emerald-900/70 hover:bg-mint"}`}>
              <Icon className={`h-4 w-4 ${active ? "text-amber-300" : ""}`} /> {label}
            </button>
          );
        })}
      </div>

      {tab === "edit" && (
        <div>
          <ProfileFields p={p} set={set} />
          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="rounded-full bg-emerald-800 px-8 text-white hover:bg-emerald-900">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save changes</>}</Button>
          </div>
        </div>
      )}

      {tab === "photos" && (
        <div className="space-y-6">
          <div className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
            <h3 className="mb-4 font-serif text-xl font-semibold text-emerald-900">Profile photo</h3>
            <PhotoManager />
          </div>
          <div>
            <h3 className="mb-3 font-serif text-xl font-semibold text-emerald-900">Privacy controls</h3>
            <PrivacyControls />
          </div>
        </div>
      )}

      {tab === "preview" && (
        <div className="mx-auto max-w-sm overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-lg animate-fade-up" data-testid="profile-preview">
          <div className="relative aspect-[4/5] bg-emerald-900">
            {p.photo ? <img src={p.photo} alt="You" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-7xl font-serif text-amber-300/50">{p.name?.[0]}</div>}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent p-5 pt-12">
              <h3 className="flex items-center gap-1.5 font-serif text-2xl font-bold text-white">{p.name}{p.age ? `, ${p.age}` : ""}{user?.verified && <BadgeCheck className="h-5 w-5 text-amber-300" />}</h3>
              <p className="flex items-center gap-1 text-emerald-100/90"><MapPin className="h-4 w-4" /> {[p.city, p.country].filter(Boolean).join(", ")}</p>
            </div>
          </div>
          <div className="space-y-3 p-5">
            <div className="flex flex-wrap gap-2">
              {[p.marital_status, p.profession, p.sect].filter(Boolean).slice(0, 2).map((c) => <span key={c} className="rounded-full bg-mint px-3 py-1 text-xs font-medium text-emerald-800">{c}</span>)}
            </div>
            {p.bio && <p className="line-clamp-3 text-sm leading-relaxed text-slate-600">{p.bio}</p>}
          </div>
          <p className="flex items-center justify-center gap-1.5 border-t border-emerald-900/10 bg-mint/30 py-2.5 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> This is how others see you</p>
        </div>
      )}
    </div>
  );
}
